export interface CommunityPrice {
    currency_primary: string;
    currency_secondary: string;
    tax_rate?: number;
    bill_in_primary: boolean;
    collect_in_secondary: boolean;
    rounding?: 'clp_integer' | 'integer' | 'two_decimals' | string;
    base_fee: number;
    included_users?: number;
    extra_user_fee?: number;
    billing?: {
        cycle: 'monthly' | 'yearly';
        day: number;
        grace_days: number;
        late_fee_percent: number;
    };
}

export interface CommunityInvoiceInfo {
    contact_email: string;
    contact_email_secondary?: string[];
    contact_name: string;
    phone?: string;
    rut_invoice?: string;
    contract_status: 'OK' | 'PENDING' | 'CANCELLED' | string;
    should_invoice: boolean;
    billing_day_hint?: string;
    due_terms?: string;
    comments?: string;
    fee_net_uf?: number;
}
// community.types.ts
export interface AddressSuggestion {
    formatted_address: string;
    lat: number;
    lng: number;
}

/** Alias para compatibilidad con código que ya importa AddressSuggestions */
export type AddressSuggestions = AddressSuggestion[];

export class Community {
    id: number = null;
    uuid: string = null;
    createdAt: Date = null;
    name: string = null;
    code: string = null;
    subscription_type: 'subscription' | 'real_estate' | 'free' | string = null;
    logo: string = null;
    updatedAt: Date = null;
    platformId: number = null;
    main_password: number;
    metadata: any = null;
    setup: CommunitySetup = null;
    //intercom: IntercomSetup = null;
    communitySize: number = null;
    remote_access: boolean;
    type: string = null;
    cityCode: string = null;
    longitude: string = null;
    latitude: string = null;
    address: string = null;
    subtype: string = null;
    queueUrl: string = null;
    status: string = null;
    countryId: number = null;
    city: CommunityCity = null;
    id_company: number = null;
    id_country: number = null;
    country: CommunityCountry = null;
    cityName: string = null;
    cityId: number = null;
    managePhone: string = null;
    real_estates: any;
    real_estate_community: any;
    mercado_pago: CommunityMercadoPago = {};
    intercom: any;
    price?: CommunityPrice | null;
    invoice_info?: CommunityInvoiceInfo | null;

    constructor(community: Partial<Community> = {}) {
        Object.assign(this, community);

        const defaultSetup: CommunitySetup = {
            offline_mode: false,
            card_number_max: null,
            sticker_number_max: null,
            add_sticker_enabled: null,
            recording_enabled: null,
            hide_private_handles: false,
            password_intercom_menu: null,
            intercom_light_timer_start: null,
            intercom_light_timer_end: null,
            active_call_concierge: false,
            user_concierge: {
                id: null,
                uuid: null,
                name: null,
                profile_pic: null,
            },
            active_call_real_estate: false,
            user_real_estate: {
                id: null,
                uuid: null,
                name: null,
                profile_pic: null,
            },
            delivery_security: {
                facial_recognition: false,
                id_recognition: false,
                self_enrollment_access: false,
                no_locker: true,
                active_notifications_resident: false,
                entry_authorized: false,
                attempt_number: 1,
                auto_scan: true,
            },
            invitations_config: {
                typeConfigUseNumber: 'user',
                numberUsesInvitations: 1,
                prevalidateInvitations: false,
                expirationTime: [],
                expirationTimeDefault: '',
                avalableDoors: [],
            },
            maintenance_security: {
                self_enrollment: false,
                attempt_number: 1,
                auto_scan: true,
                gas: false,
                water: false,
                electricity: false,
                maintenance: false,
                elevators: false,
                gardener: false,
                cleaning: false,
                garbage: false,
                others: false,
                active_call_concierge: false,
                restricted_entry: false,
                active_notifications_resident: false,
                active_notifications_concierge: false,
            },
        };
        this.setup = this.setup ? { ...defaultSetup, ...this.setup } : defaultSetup;
    }
}

export interface CommunityPagination {
    length: number;
    size: number;
    page: number;
    lastPage: number;
    startIndex: number;
    endIndex: number;
}

export interface CommunityGeneral {
    dashboard: boolean;
    report: boolean;
}

export interface CommunityUserCall {
    uuid: string;
    id: number;
    name: string;
    profile_pic: string;
}

export interface CommunityDeliverySecurity {
    facial_recognition: boolean;
    id_recognition: boolean;
    attempt_number: number;
    auto_scan: boolean;
    self_enrollment_access: boolean;
    no_locker: boolean;
    active_notifications_resident: boolean;
    entry_authorized: boolean;
}

export interface CommunityInvitationConfig {
    typeConfigUseNumber: string;
    numberUsesInvitations: number;
    prevalidateInvitations: boolean;
    expirationTime: number[];
    expirationTimeDefault: any;
    avalableDoors: any[];
}

export interface CommunityMaintenanceSecurity {
    self_enrollment: boolean;
    attempt_number: number;
    auto_scan: boolean;
    gas: boolean;
    water: boolean;
    electricity: boolean;
    maintenance: boolean;
    elevators: boolean;
    gardener: boolean;
    cleaning: boolean;
    garbage: boolean;
    others: boolean;
    active_call_concierge: boolean;
    restricted_entry: boolean;
    active_notifications_resident: boolean;
    active_notifications_concierge: boolean;
}

export interface CommunitySetup {
    offline_mode: boolean;
    intercom_light_timer_start: Date;
    intercom_light_timer_end: Date;
    card_number_max: number;
    sticker_number_max: number;
    // Tope de rostros por residente; ausente = 4. Un residente puede tener el suyo en su UserCommunity.setup.
    face_number_max?: number | null;
    add_sticker_enabled: boolean;
    recording_enabled: boolean;
    hide_private_handles: boolean;
    password_intercom_menu: string;
    active_call_concierge: boolean;
    user_concierge: CommunityUserCall;
    active_call_real_estate: boolean;
    user_real_estate: CommunityUserCall;
    delivery_security: CommunityDeliverySecurity;
    invitations_config: CommunityInvitationConfig;
    maintenance_security: CommunityMaintenanceSecurity;
}

export interface CommunityCountry {
    uuid: string;
    countryId: number;
    cities: CommunityCity[];
    name: string;
    id: number;
    prefix: string;
}

export interface CommunityCity {
    cityId: number;
    cityCode: string;
    name: string;
}

export interface CommunityMercadoPago {
    user_id?: string;
    app_number?: string;
    public_key?: string;
    access_token?: string;
}
